/* eslint consistent-return: "off" */
import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import { Box, Button } from "@chakra-ui/react";
import { exportLoans } from "api/individual-user-management";
import { IExportLoansPayload } from "api/individual-user-management/types";
import { ReactComponent as RefreshIcon } from "assets/icons/table-refresh-icon.svg";
import { useIndividuals } from "contexts/individuals-context";
import { useFilterQueryParams } from "hooks/useFilterQueryParams";
import { useUpdateQueryParams } from "hooks/useUpdateQueryParams";
import { debounce } from "lodash";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  capitalizeWords,
  formatDateToYYYYMMDD,
  handleDownloadBufferData,
  haveQueryParamsChanged,
} from "utils";
import {
  NO_EXPORT_DATA_FOUND,
  NO_NEW_FILTER_MESSAGE,
  SEARCH_DEBOUNCE_TIMER,
  ZERO,
} from "utils/constants";

import {
  customErrorToast,
  customInfoToast,
  customSuccessToast,
  customWarningToast,
} from "ui/custom-toast";
import { IDropDownRef } from "ui/dropdown/types";
import { Export } from "ui/export";
import { IExportDateRangeValues, TExportType } from "ui/export/types";
import { Filter } from "ui/filter";
import {
  IDateRangeValues,
  IFilterRef,
  TFilterOption,
  TFilterValue,
  TFilterValues,
} from "ui/filter/types";
import { tableHeaderCtaCss } from "ui/table/styles";
import { TableSearchInput } from "ui/table/table-search-input";

import {
  ALL_INDIVIDUAL_TRANSACTION_TYPE_LIST,
  DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS,
} from "../consts";
import { ITableHeaderCTAsProps } from "../types";

const LG_WIDTH = 900;
const filterOptions: TFilterOption[] = [
  {
    type: "select",
    title: "Loan Tenor",
    key: "tenor",
    placeholder: "Choose a Loan Tenor",
    options: [
      {
        label: "One Month",
        value: "1",
      },
      {
        label: "Three Months",
        value: "3",
      },
      {
        label: "Six Months",
        value: "6",
      },
      {
        label: "Nine Months",
        value: "9",
      },
      {
        label: "One year",
        value: "12",
      },
    ],
  },
  {
    type: "checkbox",
    title: "Loan Status",
    key: "status",
    options: [
      {
        label: "All",
        value: "all",
      },
      {
        label: "Running",
        value: "disbursed",
      },
      {
        label: "Completed",
        value: "paid",
      },
      {
        label: "Overdue",
        value: "overdue",
      },
      {
        label: "Closed",
        value: "closed",
      },
    ],
  },
  {
    type: "date-range",
    title: "Custom Date Range",
    key: "dateRange",
  },
];

const ALL_LOAN_STATUSES = ["all", "disbursed", "paid", "overdue", "closed"];
const AVAILABLE_EXPORT_OPTIONS: TExportType[] = ["csv", "pdf"];
export const TableHeaderCTAs = memo((props: ITableHeaderCTAsProps) => {
  const {
    handleGetTransactions,
    isLoadingTransactions,
    isRefreshing,
    setIsRefreshing,
  } = props;

  const { individualsTableHeaderCTAWidth } = useIndividuals();
  const updateQueryParams = useUpdateQueryParams();
  const { isFilterError, setIsFilterError, filterRetryCountParams } =
    useFilterQueryParams({
      apiFilterRoute: "/control/personal/user-management/loans",
    });
  const { individualId: userId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tenor = searchParams.get("tenor");
  const status = searchParams.get("status");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState<TFilterValues>({
    status: status ? status.split(",") : [],
    tenor: tenor || "",
    dateRange: {
      startDate,
      endDate,
    },
  });
  const filterRef = useRef<IFilterRef | null>(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const isFilterDisabled = !(
    filterValues?.tenor ||
    (filterValues?.dateRange as IDateRangeValues)?.startDate ||
    (filterValues?.status as string[])?.length > ZERO
  );

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // const individualsTableHeaderCTAWidth = wrapperRef.current?.offsetWidth;

  const handleResetFilter = () => {
    setFilterValues({
      status: [],
      tenor: "",
      dateRange: {
        startDate: null,
        endDate: null,
      },
    });
  };

  const onIndividualLoansSearch = useCallback(
    debounce(async (value: string) => {
      if (value.trim() === "") {
        if (userId) {
          await handleGetTransactions(
            {
              userId,
              page: Number(DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS.page),
              limit: Number(
                DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS.limit
              ),
            },
            {
              isRefetching: false,
            }
          );
        }
        return;
      }

      updateQueryParams({
        ...Object.fromEntries(new URLSearchParams(location.search)),
        userId: userId as string,
        page: String(DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS.page),
        limit: String(DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS.limit),
        search: value,
      });
    }, SEARCH_DEBOUNCE_TIMER),
    [location]
  );

  const handleSearchQueryChange = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = evt.target;
    setSearchQuery(value);
    onIndividualLoansSearch(value);
  };

  const handleFilterValueChange = (key: string, value: TFilterValue) => {
    let newValue = value;
    if (key === "status") {
      const transactionStatus = value as string[];
      const currentTransactionStatus = filterValues?.status as string[];
      if (
        transactionStatus.includes("all") &&
        currentTransactionStatus.length < ALL_LOAN_STATUSES.length
      ) {
        newValue = ALL_LOAN_STATUSES;
      } else {
        newValue = transactionStatus.filter((trxStatus) => trxStatus !== "all");
      }
    } else if (key === "transactionType") {
      const transactionType = value as string[];
      const currentTransactionType = filterValues?.transactionType as string[];
      if (
        transactionType.includes("all") &&
        currentTransactionType.length <
          ALL_INDIVIDUAL_TRANSACTION_TYPE_LIST.length
      ) {
        newValue = ALL_INDIVIDUAL_TRANSACTION_TYPE_LIST;
      } else {
        newValue = transactionType.filter((trxType) => trxType !== "all");
      }
    }
    setFilterValues((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  const handleApplyFilter = async () => {
    const newQueryParams: Record<string, string | null> = {};
    const startDate = (filterValues.dateRange as IDateRangeValues).startDate;
    const endDate = (filterValues.dateRange as IDateRangeValues).endDate;
    const status = filterValues.status;
    const tenor = filterValues.tenor as string;

    if (startDate) {
      newQueryParams["startDate"] = startDate;
    } else {
      newQueryParams["startDate"] = null;
    }

    if (endDate) {
      newQueryParams["endDate"] = endDate;
    } else {
      newQueryParams["endDate"] = null;
    }

    if (Array.isArray(status) && status.length > ZERO) {
      newQueryParams["status"] = status.join(",");
    } else {
      newQueryParams["status"] = null;
    }

    if (tenor) {
      newQueryParams["tenor"] = tenor;
    } else {
      newQueryParams["tenor"] = "";
    }

    newQueryParams["page"] =
      DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS.page.toString();
    newQueryParams["limit"] =
      DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS.limit.toString();
    setIsFilterError(false);
    updateQueryParams({
      ...newQueryParams,
      ...filterRetryCountParams,
    });
    if (
      haveQueryParamsChanged({
        currentSearch: location.search,
        newParams: newQueryParams,
      }) ||
      isFilterError
    ) {
      setIsFiltering(true);
    } else {
      customWarningToast({
        message: NO_NEW_FILTER_MESSAGE,
      });
    }
  };

  const handleRefreshTable = async () => {
    setIsRefreshing(true);
    if (!userId) {
      console.error("User id is absent!");
      return;
    }

    if (
      !haveQueryParamsChanged({
        currentSearch: location.search,
        newParams: {},
      })
    ) {
      await handleGetTransactions(
        {
          userId,
          ...DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS,
        },
        {
          isRefetching: true,
        }
      );
      setIsRefreshing(false);
    } else {
      setSearchQuery("");
      updateQueryParams({});
    }
  };

  const dropDownRef = useRef<IDropDownRef | null>(null);
  const [dateRange, setDateRange] = useState<IExportDateRangeValues>({
    startDate: null,
    endDate: null,
  });
  const [exportType, setExportType] = useState<TExportType | "">("");

  const handleExportTypeChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setExportType(value as TExportType);
  };

  const handleDateRangeChange = (name: string, value: Date | null) => {
    if (!value) return;
    setDateRange({
      ...dateRange,
      [name]: value,
    });
  };

  const handleResetExport = () => {
    setDateRange({
      startDate: null,
      endDate: null,
    });
    setExportType("");
  };

  const handleExport = async () => {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate || !userId || !exportType) return;
    const formattedStartDate = formatDateToYYYYMMDD(startDate);
    const formattedEndDate = formatDateToYYYYMMDD(endDate);
    setIsExporting(true);
    const exportPayload: IExportLoansPayload = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      format: exportType,
      userId,
    };
    try {
      const { data, error, message } = await exportLoans(exportPayload);
      if (error) {
        customErrorToast({ message });
      } else if (data?.data) {
        const exportData = data?.data;
        if (exportData.length > ZERO) {
          handleDownloadBufferData({
            data: exportData,
            name: `Loans-Information${formattedStartDate}-${formattedEndDate}`,
            type: exportType,
          });
          customSuccessToast({ message: capitalizeWords(message) });
          handleResetExport();
          if (dropDownRef.current) {
            dropDownRef.current.handleToggleDropdown();
          }
        } else {
          customInfoToast({
            message: NO_EXPORT_DATA_FOUND,
          });
        }
      } else {
        customErrorToast({
          message: "No data returned",
        });
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsExporting(false);
  };

  useEffect(() => {
    if (!isLoadingTransactions && filterRef?.current?.isFilterOpen) {
      setIsFiltering(false);
      filterRef?.current?.handleToggleFilter();
    }
  }, [isLoadingTransactions, filterRef]);

  useEffect(() => {
    if (!isLoadingTransactions && isRefreshing) {
      handleResetFilter();
      setIsRefreshing(false);
    }
  }, [isLoadingTransactions]);

  return (
    <Box
      className={`flex px-3 ${individualsTableHeaderCTAWidth < LG_WIDTH ? "flex-col py-[24.5px] justify-evenly items-start" : "items-center justify-between h-[85px]"}`}
      ref={wrapperRef}
    >
      <Box className="flex items-center gap-4">
        <TableSearchInput
          searchQuery={searchQuery}
          handleSearchQueryChange={handleSearchQueryChange}
        />
        <Filter
          options={filterOptions}
          values={filterValues}
          onChange={handleFilterValueChange}
          onApplyFilter={handleApplyFilter}
          filterBtnCss={tableHeaderCtaCss}
          isLoading={isFiltering}
          ref={filterRef}
          isApplyBtnDisabled={isFilterDisabled}
          handleResetFilter={handleResetFilter}
        />
        <Export
          isLoading={isExporting}
          handleExport={handleExport}
          dateRange={dateRange}
          exportType={exportType}
          handleExportTypeChange={handleExportTypeChange}
          handleDateRangeChange={handleDateRangeChange}
          dropDownRef={dropDownRef}
          availableExportOptions={AVAILABLE_EXPORT_OPTIONS}
          handleResetExport={handleResetExport}
        />
        <Button
          css={tableHeaderCtaCss}
          onClick={!isRefreshing ? handleRefreshTable : undefined}
          disabled={isRefreshing}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
          <p className="text-sm text-[#374151] font-semibold">Refresh</p>
        </Button>
      </Box>
    </Box>
  );
});
